import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { debounceTime, map } from 'rxjs/operators';
import { ConnectedTest, AbTestItem, AbTestApiResponse, TestGroup } from '../../strategy-catalog.models';
import { StrategyOptimizationService } from '../../services/strategy-optimization.service';
import { AbTestingApiService } from '@app/pages/checkout-app/services/api/abtesting.api.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';

@Component({
  selector: 'connect-test',
  standalone: false,
  templateUrl: './connect-test.component.html',
  styleUrl: './connect-test.component.scss',
})
export class ConnectTestComponent implements OnInit, OnChanges {
  @Input() strategyId: string;
  @Input() strategyName: string;
  @Input() isBookmarked = false;

  @Output() bookmarkChanged = new EventEmitter<boolean>();

  connectedTest: ConnectedTest | null = null;
  sectionName: string | null = null;
  testGroups: TestGroup[] = [];
  isDropdownOpen = false;
  loading = false;
  searchControl = new UntypedFormControl();

  private allTests: AbTestItem[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    private strategyOptimizationService: StrategyOptimizationService,
    private abTestingApiService: AbTestingApiService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadConnectedTest();
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.filterTests();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['strategyId'] && !changes['strategyId'].firstChange) {
      this.loadConnectedTest();
    }
    if (changes['isBookmarked']) {
      this.sectionName = this.isBookmarked
        ? this.strategyOptimizationService.getSectionNameForStrategy(this.strategyId)
        : null;
    }
  }

  onClickOutside(): void {
    if (this.isDropdownOpen) {
      this.isDropdownOpen = false;
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen && this.allTests.length === 0) {
      this.loadTests();
    }
  }

  selectTest(test: AbTestItem): void {
    this.strategyOptimizationService
      .connectTest(this.strategyId, test.testId, test.testName, test.status, this.strategyName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.connectedTest = this.strategyOptimizationService.getConnectedTest(this.strategyId);
          this.isDropdownOpen = false;
          this.searchControl.reset('');
        },
        error: () => this.toastService.showToast({ message: 'Failed to connect test', mode: 'danger' }),
      });
  }

  disconnectTest(): void {
    this.strategyOptimizationService
      .disconnectTest(this.strategyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => (this.connectedTest = null),
        error: () => this.toastService.showToast({ message: 'Failed to disconnect test', mode: 'danger' }),
      });
  }

  saveStrategy(): void {
    this.strategyOptimizationService
      .toggleStrategy(this.strategyId, this.strategyName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.isBookmarked = saved;
          this.sectionName = saved ? this.strategyOptimizationService.getSectionNameForStrategy(this.strategyId) : null;
          this.bookmarkChanged.emit(saved);
        },
        error: () => this.toastService.showToast({ message: 'Failed to save strategy', mode: 'danger' }),
      });
  }

  unsaveStrategy(): void {
    this.strategyOptimizationService
      .toggleStrategy(this.strategyId, this.strategyName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.isBookmarked = saved;
          this.sectionName = null;
          this.bookmarkChanged.emit(saved);
        },
        error: () => this.toastService.showToast({ message: 'Failed to unsave strategy', mode: 'danger' }),
      });
  }

  private loadConnectedTest(): void {
    this.connectedTest = this.strategyOptimizationService.getConnectedTest(this.strategyId);
    this.sectionName = this.isBookmarked
      ? this.strategyOptimizationService.getSectionNameForStrategy(this.strategyId)
      : null;
  }

  private loadTests(): void {
    this.loading = true;
    const params = new HttpParams().set('limit', '100').set('filter', 'ALL');
    this.abTestingApiService
      .get('ab-tests', { params })
      .pipe(
        map((tests: AbTestApiResponse[]) =>
          tests.map((t) => ({
            testId: String(t.id),
            testName: t.name,
            status: this.deriveTestStatus(t.startedAt, t.endedAt),
          })),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (tests: AbTestItem[]) => {
          this.allTests = tests;
          this.strategyOptimizationService.updateTestStatusMap(tests);
          this.filterTests();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private deriveTestStatus(startedAt: string | null, endedAt: string | null): 'draft' | 'running' | 'concluded' {
    if (startedAt && endedAt) return 'concluded';
    if (startedAt) return 'running';
    return 'draft';
  }

  private filterTests(): void {
    const searchTerm = this.searchControl.value?.trim().toLowerCase() || '';
    const filtered = searchTerm
      ? this.allTests.filter((t) => t.testName.toLowerCase().includes(searchTerm))
      : this.allTests;

    const running = filtered.filter((t) => t.status === 'running');
    const concluded = filtered.filter((t) => t.status === 'concluded');
    const draft = filtered.filter((t) => t.status === 'draft');

    this.testGroups = [
      ...(running.length > 0 ? [{ label: 'Running', tests: running }] : []),
      ...(concluded.length > 0 ? [{ label: 'Concluded', tests: concluded }] : []),
      ...(draft.length > 0 ? [{ label: 'Draft', tests: draft }] : []),
    ];
  }
}
